import _ from 'lodash';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { playSound, Sound } from '~/assets/sounds';
import { KeyButton } from '~/components/ui/KeyButton';
import { PanelControls } from '~/components/ui/PanelControls';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	CharacterSkill,
	FishRarity,
	getRandomInt,
	InputAction,
	InventoryType,
	KeyPressStates,
} from '~/shared';
import { FinishModal } from '../../components/FinishModal';
import { Indicator } from '../../components/Indicator';
import { SelectItem } from '../../components/SelectItem';
import {
	FishingStages,
	getWaitTimeRangeByDifference,
	hasFreeInventorySlot,
	type WaitTimeRangeSec,
} from './HobbyFishing.model';
import {
	StyledContainer,
	StyledPanelControlsContainer,
	StyledSelectContainer,
	StyledShadow,
	StyledUpText,
	StyledWrapper,
} from './HobbyFishing.styled';

const HobbyFishing = () => {
	const availableBaits = useObservable(rage.gameFishing.getAvaliableBaits());
	const backpackAvailable = useObservable(
		rage.gameFishing.getBackpackAvaliable(),
	);
	const hookedFish = useObservable(rage.gameFishing.getHookedFish());
	const difficulty = useObservable(rage.gameFishing.getDifficulty());
	const {
		level: hobbyFishingLevel,
		maxLevel: hobbyFishingMaxLevel,
		exp: hobbyFishingLevelExp,
		maxExp: hobbyFishingNextLevelExp,
	} = useObservable(rage.skills.getSkillLevel(CharacterSkill.HOBBY_FISHING));
	const minigameConfig = useObservable(rage.gameFishing.getMinigameConfig());
	const playerInventory = useObservable(
		rage.inventory.getInventoryByType(InventoryType.PLAYER),
	);
	const backpackInventory = useObservable(
		rage.inventory.getInventoryByType(InventoryType.BACKPACK),
	);
	const [stage, setStage] = useState<FishingStages>(FishingStages.NotStarted);
	const [isPressingButton, setIsPressingButton] = useState(false);
	const [fishingProgress, setFishingProgress] = useState(getRandomInt(15, 30));
	const [nextButton, setNextButton] = useState<'A' | 'D' | null>(null);
	const [isFinishModalVisible, setIsFinishModalVisible] = useState(false);
	const [selectedItemBait, setSelectedItemBait] = useState(0);
	const [selectedItemInventory, setSelectedItemInventory] = useState(0);
	const [hasPendingReward, setHasPendingReward] = useState(false);
	const [hasStartedFishing, setHasStartedFishing] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const waitingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [waitTimeRange, setWaitTimeRange] = useState<WaitTimeRangeSec | null>(
		null,
	);
	const [isErrorState, setIsErrorState] = useState(false);
	const [errorProgress, setErrorProgress] = useState(0);
	const errorProgressRef = useRef(0);

	const isBackpackSelected = selectedItemInventory === 1;
	const isInventoryFull = !hasFreeInventorySlot(
		isBackpackSelected ? backpackInventory : playerInventory,
	);

	const getUpTextContent = () => {
		if (stage === FishingStages.NotStarted && selectedItemBait === 0) {
			return 'Выберите приманку, чтобы забросить удочку';
		}
		if (stage === FishingStages.NotStarted) {
			return (
				<Fragment>
					Нажмите <KeyButton keyName="Space" />, чтобы забросить удочку
				</Fragment>
			);
		}
		if (stage === FishingStages.Waiting) {
			return 'Ожидайте клёва...';
		}
		if (stage === FishingStages.CatchFishTypeA) {
			return (
				<Fragment>
					Зажмите <KeyButton keyName="Space" />, чтобы тянуть рыбу
				</Fragment>
			);
		}
		if (stage === FishingStages.CatchFishTypeB) {
			return (
				<Fragment>
					Нажимайте <KeyButton keyName="A" /> и <KeyButton keyName="D" />, чтобы
					измотать рыбу
				</Fragment>
			);
		}
		return null;
	};

	const stageRef = useRef(stage);
	const isPressingButtonRef = useRef(isPressingButton);
	const nextButtonRef = useRef(nextButton);
	const isFinishModalVisibleRef = useRef(isFinishModalVisible);
	const selectedItemBaitRef = useRef(selectedItemBait);
	const availableBaitsRef = useRef(availableBaits);
	const isErrorStateRef = useRef(isErrorState);
	const difficultyRef = useRef(difficulty);

	useEffect(() => {
		stageRef.current = stage;
	}, [stage]);

	useEffect(() => {
		isPressingButtonRef.current = isPressingButton;
	}, [isPressingButton]);

	useEffect(() => {
		nextButtonRef.current = nextButton;
	}, [nextButton]);

	useEffect(() => {
		isFinishModalVisibleRef.current = isFinishModalVisible;
	}, [isFinishModalVisible]);

	useEffect(() => {
		selectedItemBaitRef.current = selectedItemBait;
	}, [selectedItemBait]);

	useEffect(() => {
		availableBaitsRef.current = availableBaits;
	}, [availableBaits]);

	useEffect(() => {
		errorProgressRef.current = errorProgress;
	}, [errorProgress]);

	useEffect(() => {
		isErrorStateRef.current = isErrorState;
	}, [isErrorState]);

	useEffect(() => {
		difficultyRef.current = difficulty;
	}, [difficulty]);

	const setWrongPress = () => {
		if (!isErrorState) {
			setIsErrorState(true);
		}
	};

	const clearWrongPress = () => {
		if (isErrorState) {
			setIsErrorState(false);
		}
	};

	useEffect(() => {
		if (
			stage !== FishingStages.CatchFishTypeA &&
			stage !== FishingStages.CatchFishTypeB
		) {
			if (errorProgressRef.current !== 0) {
				setErrorProgress(0);
			}

			return;
		}

		const fullRedMs = difficulty.wrongControlFullRedSec * 1000;
		const stepMs = 50;
		const delta = stepMs / fullRedMs;

		const interval = setInterval(() => {
			setErrorProgress((prev) => {
				let next = prev;

				if (isErrorState) {
					next = Math.min(1, prev + delta);
				} else {
					next = Math.max(0, prev - delta);
				}

				if (next >= 1 && prev < 1) {
					rage.gameFishing
						.reportLoss()
						.catch((err) => rage.notifications.showException(err));
					rage.notifications.showInfo('Вы упустили рыбу!');
					setStage(FishingStages.NotStarted);
					setIsErrorState(false);
					setNextButton(null);
				}

				return next;
			});
		}, stepMs);

		return () => clearInterval(interval);
	}, [difficulty, isErrorState, stage]);

	const handleStartRef = useRef<() => void>(() => {});
	handleStartRef.current = () => {
		const currentStage = stageRef.current;
		const currentIsFinishModalVisible = isFinishModalVisibleRef.current;
		const currentSelectedItemBait = selectedItemBaitRef.current;

		if (
			currentStage !== FishingStages.NotStarted ||
			currentIsFinishModalVisible
		) {
			return;
		}

		if (currentSelectedItemBait === 0) {
			rage.notifications.showError('Приманка не выбрана');
			return;
		}

		if (isInventoryFull) {
			rage.notifications.showError('У вас переполнен инвентарь');

			if (!hasStartedFishing) {
				return;
			}
		}

		const currentAvailableBaits = availableBaitsRef.current;
		const currentSelectedBaitItemId =
			currentSelectedItemBait > 0
				? currentAvailableBaits[currentSelectedItemBait - 1]?.itemId
				: undefined;

		rage.gameFishing
			.start(currentSelectedBaitItemId)
			.catch((err) => rage.notifications.showException(err));
		setWaitTimeRange(null);
		setFishingProgress(
			getRandomInt(
				difficultyRef.current.startProgressMin,
				difficultyRef.current.startProgressMax,
			),
		);
		isPressingButtonRef.current = false;
		setIsPressingButton(false);
		playSound(Sound.StartFishing);
		setStage(FishingStages.Waiting);
		setHasStartedFishing(true);
	};

	const handleThrow = useCallback(
		(difference: number) => {
			setWaitTimeRange(
				getWaitTimeRangeByDifference(difference, minigameConfig),
			);
		},
		[minigameConfig],
	);

	const handleCatchADRef = useRef<(button: 'A' | 'D') => void>(() => {});
	handleCatchADRef.current = (button: 'A' | 'D') => {
		const currentStage = stageRef.current;
		const currentIsPressingButton = isPressingButtonRef.current;
		const currentNextButton = nextButtonRef.current;

		if (currentStage === FishingStages.CatchFishTypeA) {
			// В стадии, где требуется пробел, A/D считаются неправильными нажатиями
			setWrongPress();
			return;
		}

		if (
			currentStage !== FishingStages.CatchFishTypeB ||
			currentIsPressingButton
		) {
			return;
		}

		if (button === currentNextButton || currentNextButton === null) {
			clearWrongPress();
			setFishingProgress((prev) =>
				Math.min(
					prev +
						_.random(
							difficultyRef.current.tugGainMin,
							difficultyRef.current.tugGainMax,
						),
					100,
				),
			);
			const newNextButton = button === 'A' ? 'D' : 'A';
			nextButtonRef.current = newNextButton;
			setNextButton(newNextButton);
		} else {
			setWrongPress();
		}
	};

	const handleExitRef = useRef<() => void>(() => {});
	handleExitRef.current = () => {
		if (stageRef.current !== FishingStages.NotStarted) {
			rage.gameFishing
				.stop()
				.catch((err) => rage.notifications.showException(err));
		}

		rage.gameFishing
			.exit()
			.catch((err) => rage.notifications.showException(err));
	};

	const handleCatchA = useCallback(() => {
		handleCatchADRef.current('A');
	}, []);

	const handleCatchD = useCallback(() => {
		handleCatchADRef.current('D');
	}, []);

	useInputAction(InputAction.HobbyFishingCatchFishA, handleCatchA);

	useInputAction(InputAction.HobbyFishingCatchFishD, handleCatchD);

	const handleSpaceRef = useRef<(state: KeyPressStates) => void>(() => {});
	handleSpaceRef.current = (state: KeyPressStates) => {
		const isPressed = state === KeyPressStates.Pressed;
		isPressingButtonRef.current = isPressed;
		setIsPressingButton(isPressed);

		if (!isPressed) {
			return;
		}

		if (stageRef.current === FishingStages.CatchFishTypeA) {
			clearWrongPress();
		} else if (stageRef.current === FishingStages.CatchFishTypeB) {
			setWrongPress();
		}
	};

	const handleSpace = useCallback((state: KeyPressStates) => {
		handleSpaceRef.current(state);
	}, []);

	useInputAction(InputAction.HobbyFishingCatchFishSpace, handleSpace, {
		state: KeyPressStates.Both,
	});

	const handleStart = useCallback(() => {
		handleStartRef.current();
	}, []);

	useInputAction(InputAction.HobbyFishingStart, handleStart);

	const handleExit = useCallback(() => {
		handleExitRef.current();
	}, []);

	useInputAction(InputAction.CommonMenuClose, handleExit);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}

			if (waitingTimeoutRef.current) {
				clearTimeout(waitingTimeoutRef.current);
				waitingTimeoutRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (stage !== FishingStages.CatchFishTypeA) {
			return;
		}

		const interval = setInterval(() => {
			// if (Math.random() < minigameConfig.fishEscapeChance) {
			// 	rage.gameFishing.reportLoss();
			// 	rage.notifications.showInfo('Рыба сорвалась с крючка!');
			// 	setStage(FishingStages.NotStarted);
			// 	return;
			// }

			setFishingProgress((prev) => {
				const isPressing = isPressingButtonRef.current;
				const baseDelta = isPressing
					? _.random(difficulty.holdGainMin, difficulty.holdGainMax)
					: -_.random(difficulty.idleDrainMin, difficulty.idleDrainMax);

				const speedMultiplier = Math.max(0, 1 - errorProgressRef.current);

				if (speedMultiplier === 0) {
					return prev;
				}

				const next = prev + baseDelta * speedMultiplier;

				if (next >= 100) {
					return 100;
				}

				if (next <= 0) {
					return 0;
				}

				return next;
			});
		}, 50);

		return () => clearInterval(interval);
	}, [difficulty, stage]);

	useEffect(() => {
		if (stage !== FishingStages.CatchFishTypeB) {
			return;
		}

		const interval = setInterval(() => {
			// if (Math.random() < minigameConfig.fishEscapeChance) {
			// 	rage.gameFishing.reportLoss();
			// 	rage.notifications.showInfo('Рыба сорвалась с крючка!');
			// 	setStage(FishingStages.NotStarted);
			// 	return;
			// }

			setFishingProgress((prev) => {
				const baseDelta = -_.random(
					difficulty.tugDrainMin,
					difficulty.tugDrainMax,
				);

				const speedMultiplier = Math.max(0, 1 - errorProgressRef.current);

				if (speedMultiplier === 0) {
					return prev;
				}

				const next = prev + baseDelta * speedMultiplier;

				if (next <= 0) {
					return 0;
				}

				return next;
			});
		}, 100);

		return () => clearInterval(interval);
	}, [difficulty, stage]);

	useEffect(() => {
		if (selectedItemBait > availableBaits.length) {
			setSelectedItemBait(0);
		}
	}, [availableBaits.length, selectedItemBait]);

	useEffect(() => {
		if (stage === FishingStages.NotStarted) {
			return;
		}

		if (fishingProgress <= 0) {
			rage.gameFishing
				.reportLoss()
				.catch((err) => rage.notifications.showException(err));
			rage.notifications.showInfo('Вы упустили рыбу!');
			setStage(FishingStages.NotStarted);
		}

		if (fishingProgress >= 100) {
			playSound(Sound.Fishing1);
			setFishingProgress(0);
			setStage(FishingStages.NotStarted);
			setHasPendingReward(true);
			rage.gameFishing
				.reportWin(isBackpackSelected)
				.catch((err) => rage.notifications.showException(err));
		}
	}, [fishingProgress, stage]);

	useEffect(() => {
		if (!hasPendingReward || !hookedFish) {
			return;
		}

		setIsFinishModalVisible(true);
		setHasPendingReward(false);
	}, [hasPendingReward, hookedFish]);

	useEffect(() => {
		if (stage !== FishingStages.Waiting && stage !== FishingStages.NotStarted) {
			timeoutRef.current = setTimeout(
				() => {
					setStage(
						stage === FishingStages.CatchFishTypeA
							? FishingStages.CatchFishTypeB
							: FishingStages.CatchFishTypeA,
					);
				},
				getRandomInt(difficulty.phaseSwitchMsMin, difficulty.phaseSwitchMsMax),
			);

			return () => {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
					timeoutRef.current = null;
				}
			};
		}
	}, [difficulty, stage]);

	useEffect(() => {
		if (
			stage === FishingStages.CatchFishTypeA ||
			stage === FishingStages.CatchFishTypeB
		) {
			return;
		}

		clearWrongPress();
	}, [stage]);

	useEffect(() => {
		if (stage !== FishingStages.NotStarted) {
			return;
		}
	}, [stage]);

	useEffect(() => {
		if (stage === FishingStages.Waiting && waitTimeRange) {
			const delayMs = getRandomInt(
				waitTimeRange.minSec * 1000,
				waitTimeRange.maxSec * 1000,
			);
			waitingTimeoutRef.current = setTimeout(() => {
				playSound(Sound.FishFind);
				setFishingProgress(
					getRandomInt(
						difficulty.startProgressMin,
						difficulty.startProgressMax,
					),
				);
				setStage(
					getRandomInt(1, 2) === 1
						? FishingStages.CatchFishTypeA
						: FishingStages.CatchFishTypeB,
				);
			}, delayMs);

			return () => {
				if (waitingTimeoutRef.current) {
					clearTimeout(waitingTimeoutRef.current);
					waitingTimeoutRef.current = null;
				}
			};
		}
	}, [difficulty, stage, waitTimeRange]);

	return (
		<StyledWrapper>
			<StyledContainer>
				<StyledShadow />
				<StyledSelectContainer>
					<SelectItem
						type="inventory"
						backpackAvailable={backpackAvailable}
						disabled={stage !== FishingStages.NotStarted}
						selectedItem={selectedItemInventory}
						setSelectedItem={setSelectedItemInventory}
					/>
					<SelectItem
						type="bait"
						baitItems={availableBaits}
						disabled={stage !== FishingStages.NotStarted}
						selectedItem={selectedItemBait}
						setSelectedItem={setSelectedItemBait}
					/>
				</StyledSelectContainer>
				<StyledUpText>{getUpTextContent()}</StyledUpText>
				<Indicator
					stage={stage}
					fishingProgress={fishingProgress}
					handleThrow={handleThrow}
					errorProgress={errorProgress}
				/>
			</StyledContainer>
			<StyledPanelControlsContainer>
				{(stage === FishingStages.CatchFishTypeA ||
					stage === FishingStages.CatchFishTypeB) && (
					<Fragment>
						<PanelControls
							align="right"
							controls={[
								{
									text: 'Тянуть рыбу',
									keys: ['Space'],
								},
							]}
						/>
						<PanelControls
							align="right"
							controls={[
								{
									text: 'Измотать рыбу',
									keys: ['D', 'A'],
								},
							]}
						/>
					</Fragment>
				)}
				{stage === FishingStages.NotStarted && (
					<PanelControls
						align="right"
						controls={[
							{
								text: 'Забросить удочку',
								keys: ['Space'],
								onClick: handleStart,
							},
						]}
					/>
				)}
				<PanelControls
					align="right"
					controls={[
						{
							text: 'Закончить рыбалку',
							keys: ['Esc'],
							onClick: handleExit,
						},
					]}
				/>
			</StyledPanelControlsContainer>
			<CSSTransition
				in={isFinishModalVisible}
				timeout={300}
				classNames="modal"
				unmountOnExit
			>
				<FinishModal
					fishId={hookedFish?.fishId ?? ''}
					fishType={hookedFish?.rarity ?? FishRarity.COMMON}
					name={hookedFish?.name ?? ''}
					level={hobbyFishingLevel}
					maxLevel={hobbyFishingMaxLevel}
					weight={hookedFish?.weight ?? 0}
					exp={hookedFish?.expCatch ?? 0}
					expRelease={hookedFish?.expRelease ?? 0}
					description={hookedFish?.description ?? ''}
					levelExp={hobbyFishingLevelExp}
					nextLevelExp={hobbyFishingNextLevelExp}
					handleClose={() => setIsFinishModalVisible(false)}
				/>
			</CSSTransition>
		</StyledWrapper>
	);
};

export { HobbyFishing };
